import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { Button, CircularProgress, Menu, MenuItem } from "@mui/material";
import * as hooks from "lib/hooks";
import * as result from "lib/result";
import { CsvUploadTemplateByXlsx } from "lib/util/constants";
import * as util from "lib/util";
import { useState } from "react";

export type TemplateFormat = "csv" | "xlsx";

/** Menu order: CSV first, then Excel (SITMP-8371 mockup). */
const templateFormatOptions: { format: TemplateFormat; label: string }[] = [
	{ format: "csv", label: "CSV (.csv)" },
	{ format: "xlsx", label: "Excel (.xlsx)" },
];

/** Blob name of the template in the requested format; templates are keyed by their .xlsx name. */
export function TemplateFileNameForFormat(
	xlsxFileName: string,
	format: TemplateFormat
): string {
	if (format === "xlsx") return xlsxFileName;
	return (
		CsvUploadTemplateByXlsx[xlsxFileName] ??
		xlsxFileName.replace(/\.xlsx$/i, ".csv")
	);
}

/** Fetches a template blob and saves it under its own file name. */
export async function DownloadTemplateFile(
	mccApi: ReturnType<typeof hooks.UseMccApi>,
	fileName: string
): Promise<void> {
	const blob = await mccApi.DownloadExcelTemplate(fileName);
	const url = window.URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = fileName;
	document.body.appendChild(a);
	a.click();
	a.remove();
	window.URL.revokeObjectURL(url);
}

interface downloadTemplateMenuProps {
	label: string;
	/**
	 * Called with the picked format. When omitted, the menu downloads
	 * {@link xlsxFileName} (or its CSV counterpart) itself.
	 */
	onDownload?: (format: TemplateFormat) => void | Promise<void>;
	/** .xlsx template name; required when {@link onDownload} is omitted. */
	xlsxFileName?: string;
	/** Controlled loading state; otherwise tracked internally. */
	downloading?: boolean;
	disabled?: boolean;
	testSelector?: string;
}

/**
 * One outline "label ▾" button; both template formats live in its menu.
 * No format is remembered between downloads.
 */
export function DownloadTemplateMenu({
	label,
	onDownload,
	xlsxFileName,
	downloading: controlledDownloading,
	disabled = false,
	testSelector = "download-template",
}: downloadTemplateMenuProps): React.JSX.Element {
	const mccApi = hooks.UseMccApi();
	const snack = hooks.UseSnackBar();
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [internalDownloading, setInternalDownloading] = useState(false);
	const downloading = controlledDownloading ?? internalDownloading;
	const open = Boolean(anchorEl);
	const menuId = `${testSelector}-menu`;

	const handlePick = async (format: TemplateFormat) => {
		setAnchorEl(null);
		if (onDownload) {
			await onDownload(format);
			return;
		}
		if (!xlsxFileName) return;
		setInternalDownloading(true);
		try {
			await DownloadTemplateFile(
				mccApi,
				TemplateFileNameForFormat(xlsxFileName, format)
			);
			snack.open(result.Ok("Template downloaded successfully"));
		} catch (e) {
			snack.open(result.Err(util.GetMsgFromError(e as Error)));
		} finally {
			setInternalDownloading(false);
		}
	};

	return (
		<>
			<Button
				variant="outlined"
				color="primary"
				onClick={(e) => setAnchorEl(e.currentTarget)}
				disabled={disabled || downloading}
				aria-haspopup="menu"
				aria-controls={open ? menuId : undefined}
				aria-expanded={open ? "true" : undefined}
				endIcon={
					downloading ? (
						<CircularProgress size={16} thickness={5} />
					) : (
						<ArrowDropDownIcon />
					)
				}
				data-test-selector={testSelector}
				sx={{
					alignSelf: "flex-start",
					borderRadius: "8px",
					textTransform: "none",
					fontWeight: 600,
					fontSize: "0.95rem",
					px: 2.5,
					py: 1,
				}}
			>
				{label}
			</Button>
			<Menu
				id={menuId}
				anchorEl={anchorEl}
				open={open}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
				slotProps={{
					paper: { sx: { mt: 0.5, borderRadius: 2, minWidth: 180 } },
				}}
			>
				{templateFormatOptions.map(({ format, label: optionLabel }) => (
					<MenuItem
						key={format}
						onClick={() => handlePick(format)}
						data-test-selector={`${testSelector}-${format}`}
						sx={{ fontWeight: 600, fontSize: "0.9rem", py: 1.25 }}
					>
						{optionLabel}
					</MenuItem>
				))}
			</Menu>
		</>
	);
}
